import _ from 'lodash';

/**
 * Calculate backdrop URL for proposal sections
 * Uses complex logic to handle virtual events, green screen priority, and backdrop fallbacks
 *
 * @param {object} event - Full event object
 * @param {string} sectionId - Section identifier (e.g., 'photos', 'greenScreen', 'masks')
 * @returns {string} Backdrop URL
 */
export const calculateBackdrop = (event, sectionId) => {
  const isVirtual = event.eventType === 'virtual';

  // For virtual events, use the first background set for photo background removal
  const photoBackgroundUrl = _.get(event, 'captureConfig.greenScreens[0].background[0]');
  const photoBackgroundEnabled = _.get(event, 'captureConfig.magicGreenScreenEnabled');
  if (isVirtual && photoBackgroundUrl && photoBackgroundEnabled) {
    return photoBackgroundUrl;
  }

  // If we have a custom backdrop set, use that, however,
  // we need to prefer green screen background for the green screen section
  const isBackdropEnabled = _.get(event, 'proposalConfig.backdrop.enabled', false);
  const greenScreenEnabled = _.get(event, 'captureConfig.magicGreenScreenEnabled', false);
  const greenScreen = _.get(event, 'captureConfig.greenScreens', []).find((gs) => gs.isEnabled);

  // Use backdrop unless we're on greenScreen/greenScreens section and green screen is enabled
  const isGreenScreenSection = sectionId === 'greenScreen' || sectionId === 'greenScreens';
  if (isBackdropEnabled && !(greenScreenEnabled && greenScreen && isGreenScreenSection)) {
    if (_.get(event, 'proposalConfig.backdrop.url')) {
      return event.proposalConfig.backdrop.url;
    }
    if (_.get(event, 'proposalConfig.backdrop.id')) {
      const bd = event.proposalConfig.backdrop.id.split('-');
      return `/static/backdrops/${bd[0]}/${bd[1]}-full.jpg`;
    }
  }

  // If we have a green screen background enabled, use that
  const isPhotoOrBackdrop = sectionId === 'photos' || sectionId === 'backdrop' || sectionId === 'backdropSection';
  if (greenScreenEnabled && greenScreen && greenScreen.background && greenScreen.background[0] && isPhotoOrBackdrop) {
    // If we have to show print template, infer the thumbnail URL
    const url = greenScreen.background[0];
    if (url.includes('/asset-library/')) {
      // Infer thumbnail URL for PBSCO-provided assets
      return url
        .replace('/asset-library/', '/asset-thumbnails/')
        .replace('/videos/', '/images/')
        .replace(/\.[^.]+$/, '.jpg');
    }
    // Infer thumbnail URL for user-provided assets
    return url.replace(/\.[^.]+$/, '.thumb.png');
  }

  if (
    greenScreenEnabled &&
    greenScreen &&
    (sectionId !== 'photos' || (sectionId === 'photos' && !event.printTemplate))
  ) {
    if (greenScreen.background && greenScreen.background[0]) {
      return greenScreen.background[0];
    }
  }

  return '/static/backdrops/blank/0-full.jpg';
};
